import {EventEmitter, Injectable, OnDestroy, TemplateRef} from '@angular/core';
import {FormArray, FormBuilder, FormControl, FormGroup, ValidatorFn, Validators} from '@angular/forms';
import {BehaviorSubject, combineLatest, forkJoin, from, merge, Observable, of, ReplaySubject, Subject, throwError} from 'rxjs';
import {productionPODValidator, validateNotBlank} from '../../../commons/validators';
import {
    ATECO_PATTERN,
    DOCUMENT_TYPE_PATTERN,
    EMAIL_PATTERN,
    IBAN_PATTERN,
    LAND_REGISTRY_DATA_PATTERN,
    MOBILE_PATTERN,
    NAME_PATTERN,
    TAX_ID_PATTERN,
    VAT_NUMBER_PATTERN
} from '../../../shared/patterns';
import {
    ECEntitySubtype,
    ECJuridicalType,
    ECLandRegistryData,
    ECLegalForm,
    ECMember,
    ECMemberAccessType,
    ECMemberParticipationType,
    ECMemberPersonalData,
    ECMemberPOD,
    ECMemberPropertyUnit,
    ECMemberRelation,
    ECMemberRelationTargetType,
    ECPodCreationType,
    ECPodUtilityType,
    ECSubjectType,
    EnergyCommunityNamedPod,
    NewECMemberRequest,
    PlantStatisticalProfile,
    PodStatisticalProfile,
    SpatialCoordinates,
    UpdateECMemberRequest
} from 'smag-commons/cfg/hierarchy';
import {
    ConfigurableField,
    CustomerConfig,
    FeaturesConfig,
    FieldConfig,
    FieldConfigScope,
    ProducerPodStatisticalProfileRequest
} from 'smag-commons/dto/api';
import {
    FormError,
    getIncentiveTypes,
    getPlantType,
    getPodStatisticalProfileRequestPlantType,
    getPodUtilityTypes,
    isLandRegistryDataValid,
    mkMemberName,
    mkTextField,
    PlantType,
    PodEnergyProfiles,
    PV_PLANT_TYPE,
    showPropertyUnit,
    WIND_PLANT_TYPE
} from '../../../shared/energy-community';
import {catchError, debounceTime, filter, first, map, pairwise, startWith, switchMap, takeUntil, tap, withLatestFrom} from 'rxjs/operators';
import * as moment from 'moment-timezone';
import {googleCompatibleTextAddress, isDefined, isProducerEntity, ITALY, mkMemberAddress} from 'smag-commons/fcn';
import {findMemberTargetPodRelation, getPodHolders, validateDocumentType} from '../../../shared/ec-members';
import {Phases, RateMode, VoltageLevel} from 'smag-commons/cfg';
import {SmagApiService} from '../../../api/smag-api.service';
import {ECMemberAddress, ECMemberDocument} from 'smag-commons/cfg/hierarchy/ec';
import {COUNTRIES, Country, LOCALIZATIONS as COUNTRY_LOCALIZATIONS} from '../../../shared/countries';
import {ENTITY_NAME_VALIDATION_PATTERN} from '../../../shared/entity-names';
import {CAPITAL_GRANT_MAX, CAPITAL_GRANT_MIN, UPPER_INCENTIVE_BOUNDARY} from '../../../shared/ec-economics';
import {I18NService} from '../../../i18n/i18n.service';
import {errorCode, isBlank} from '../../../commons/commons';
import {ConfigurationService} from '../../../shared/configuration.service';
import {DialogService} from '../../../shared/dialog/dialog.service';
import {MsgBoxService} from '../../../shared/msg-box.service';
import {Member} from '../ec-members.component';
import {AddressExt} from '../../../shared/google-maps-address-autocomplete/google-maps-address-autocomplete.component';
import {MUNICIPALITIES} from '../../../shared/municipalities';
import {PredictiveModelInfo} from 'smag-commons/cfg/prediction';
import {getRateMode} from 'src/app/shared/statistical-profiles';
import {SpinnerData} from '../../../shared/spinner/spinner.component';
import {ErrorCode} from '../../../api/errors';
import {Document, EditedDocument, FormCommunity, MemberRange, MembersInfo, PodInfo, PropertyUnitFormError} from './new-member.model';
import {
    addConfigurableControls,
    addConfigurableControlsWithAdditionalValidator,
    createEcMemberPod,
    dateOrderValidator,
    getDefaultEnergyProfile,
    getMatchedConditions,
    handlePropertyUnitValidation,
    isFieldEnabled,
    isLandRegistryDataValued,
    isPersistentPod,
    isPropertyUnitForbidden,
    isPropertyUnitLocalDuplicate,
    isSameDocument,
    mkEnergyCommunityNamedPlant,
    mkEnergyCommunityNamedPod,
    mkErrorMessage,
    mkMemberForConfig,
    mkNewPod,
    mkPodRelations,
    mkPropertyUnitKey,
    mkPropertyUnitRelations,
    mkPropertyUnitTmpId,
    mkPropertyUnitTriplet,
    normalizeRange,
    plantControls,
    plantsFormArray,
    podExists,
    podIsInvalid,
    rangeTaken,
    resetPodStatisticalProfile,
    setConfigurableControlsValues,
    setConfigurableDynamicControl,
    validateIfMandatory,
    validateMemberForm,
    validatePOD,
    validatePodControl
} from './new-member.utils';
import {HierarchyService} from '../../../shared/hierarchy.service';


export const EC_DOCUMENT_TYPE_OTHER = 'Other';
export const INTERNAL_DOCUMENT_PREFIX = 'internal_';
const ALLOWED_COUNTRIES = [ITALY];
const TYPES_DEBOUNCE_MILLIS = 250;
const DEFAULT_JURIDICAL_TYPE = ECJuridicalType.INDIVIDUAL;
const PARTICIPATION_TYPES: ECMemberParticipationType[] = [
    ECMemberParticipationType.MEMBER,
    ECMemberParticipationType.THIRD_PARTY_PRODUCER
];
const ACCESS_TYPES: ECMemberAccessType[] = [
    ECMemberAccessType.MOBILE,
    ECMemberAccessType.ADVANCED,
    ECMemberAccessType.NONE
];

// the following flag allows/prevents creating member-POD relations starting from the future (wrt. "now")
// setting this to `true` means also allowing re-relating members to PODs if the previously related member (if any)
// has set a relation end date in the future
const ALLOW_FUTURE_MEMBER_POD_RELATIONS = false;


@Injectable()
export class MemberFormService implements OnDestroy {
    // tslint:disable:variable-name
    private readonly _destroyed = new Subject<void>();
    private readonly _documents: { [id: string]: Document } = {};
    private _memberForm: FormGroup;
    private _propertyUnitsForm: FormGroup;
    private _podsForm: FormGroup;
    private _countriesByCode: { [code: string]: Country } = {};
    private _countries: Country[] = [];
    private _currentCoordinates: SpatialCoordinates = null;
    private _customerCfg: CustomerConfig;
    private _fieldConfigs: Map<ConfigurableField, FieldConfig>;
    private _currentAddress: ECMemberAddress = null;
    private _timeZone: string = null;
    private _featureConfig: FeaturesConfig;
    private _ecMember: ECMember;
    private _energyDataStepEnabled: boolean;
    private _statisticalProfileStepEnabled: boolean;
    private _energyDataStepBusy = false;
    private _documentTypes: ReplaySubject<string[]> = new ReplaySubject<string[]>(1);
    private _accessTypeFormControl: FormControl = new FormControl(null, []);
    private _saving = false;
    private _participationTypes: ECMemberParticipationType[] = PARTICIPATION_TYPES;
    private _accessTypes: ECMemberAccessType[] = [ECMemberAccessType.NONE];
    private _member: Member;
    private _community: FormCommunity;
    private _spinnerDialog: TemplateRef<any>;
    private _adviceDialog: TemplateRef<any>;
    private _closeDialog: EventEmitter<boolean>;
    private _propertyUnitDateMap: Map<string, MemberRange> = new Map(); // used to prevent self range overlap check on edited pu range
    private _podAndLrdMap: Map<string, ECLandRegistryData> = new Map(); // used to hide forbidden ($$...$$) land registry data in the pod form fields and prevent the loss of those values
    // tslint:enable:variable-name

    constructor(private readonly apiService: SmagApiService,
                private readonly hierarchyService: HierarchyService,
                private readonly configurationService: ConfigurationService,
                private readonly dialogService: DialogService,
                private readonly i18n: I18NService,
                private readonly msgBoxService: MsgBoxService,
                private readonly fb: FormBuilder) {
        this.initCountries();
        this.initForms();
    }

    ngOnDestroy() {
        this._destroyed.next();
        this._destroyed.complete();
    }

    get documents() {
        return this._documents;
    }

    get fieldConfigs() {
        return this._fieldConfigs;
    }

    get featureConfig() {
        return this._featureConfig;
    }

    get documentTypes() {
        return this._documentTypes;
    }

    get memberForm() {
        return this._memberForm;
    }

    get propertyUnitsForm() {
        return this._propertyUnitsForm;
    }

    get podsForm() {
        return this._podsForm;
    }

    get timeZone(): string {
        return this._timeZone;
    }

    get energyDataStepEnabled(): boolean {
        return this._energyDataStepEnabled;
    }

    get statisticalProfileStepEnabled(): boolean {
        return this._statisticalProfileStepEnabled;
    }

    get energyDataStepBusy(): boolean {
        return this._energyDataStepBusy;
    }

    get accessTypeFormControl(): FormControl {
        return this._accessTypeFormControl;
    }

    get countries(): Country[] {
        return this._countries;
    }

    get saving(): boolean {
        return this._saving;
    }

    get participationTypes(): ECMemberParticipationType[] {
        return this._participationTypes;
    }

    get accessTypes(): ECMemberAccessType[] {
        return this._accessTypes;
    }

    get isCompany(): boolean {
        return !(this.juridicalType === ECJuridicalType.INDIVIDUAL && this.legalFormType === ECLegalForm.OTHER_INDIVIDUALS);
    }

    get juridicalType(): ECJuridicalType {
        return this._memberForm.controls.juridicalType.value;
    }

    get isCorporateEntity() {
        return this.juridicalType === ECJuridicalType.CORPORATE_ENTITY;
    }

    get legalFormType(): ECLegalForm {
        return this._memberForm.controls.legalForm?.value ?? null;
    }

    get entitySubtype(): ECEntitySubtype {
        return this._memberForm.controls.entitySubtype.value;
    }

    get subjectType(): ECSubjectType {
        return this._memberForm.controls.subjectType.value;
    }

    get participationType(): ECMemberParticipationType {
        return this._memberForm.controls.participationType.value ?? '';
    }

    get isMember() {
        return this.participationType === ECMemberParticipationType.MEMBER;
    }

    get isMobileMember() {
        return this.isMember && this.getAccessType === ECMemberAccessType.MOBILE;
    }

    get isThirdPartyProducer() {
        return this.participationType === ECMemberParticipationType.THIRD_PARTY_PRODUCER;
    }

    get ownersAvailable() {
        return !!this._featureConfig?.owners;
    }

    get hasSEU(): boolean {
        const podControls = this._podsForm.controls.pods.value as any[];
        return podControls.some(c => c.seu);
    }

    get isUpdate(): boolean {
        return !!this._member;
    }

    get addressFormControl(): FormControl {
        return this._memberForm.controls[ConfigurableField.ADDRESS] as FormControl;
    }

    get getAccessType(): ECMemberAccessType {
        return this._accessTypeFormControl.value || this._ecMember?.access_type;
    }

    get documentsFormArray(): FormArray {
        return this._memberForm.controls.documents as FormArray;
    }

    get documentControls(): FormGroup[] {
        return (this.documentsFormArray).controls as FormGroup[];
    }


    get propertyUnitsFormArray(): FormArray {
        return this._propertyUnitsForm.controls.propertyUnits as FormArray;
    }

    get propertyUnitControls(): FormGroup[] {
        return (this.propertyUnitsFormArray).controls as FormGroup[];
    }

    get podsFormArray(): FormArray {
        return this._podsForm.controls.pods as FormArray;
    }

    get podControls(): FormGroup[] {
        return (this.podsFormArray).controls as FormGroup[];
    }

    get bornInItaly(): boolean {
        // look for an already set country; if none, fall back on currently edited value
        let birthCountryCode = this._ecMember?.personal_data.birth_country;
        if (!birthCountryCode) {
            const editedBirthCountry: Country = this._memberForm?.controls[ConfigurableField.BIRTH_COUNTRY]?.value;
            if (editedBirthCountry) {
                birthCountryCode = editedBirthCountry.code;
            }
        }
        return birthCountryCode === 'IT';
    }

    get hasPropertyUnits(): boolean {
        return !!this.propertyUnitControls.length;
    }

    get hasPods(): boolean {
        return !!this.podControls.length;
    }

    get hasOnlyPropertyUnits(): boolean {
        return this.hasPropertyUnits && !this.hasPods;
    }

    get hasOnlyPods(): boolean {
        return !this.hasPropertyUnits && this.hasPods;
    }

    get hasPropertyUnitsOrPods(): boolean {
        return this.hasPropertyUnits || this.hasPods;
    }

    init(member: Member, community: FormCommunity, closeDialog: EventEmitter<boolean>) {
        this._member = member;
        this._ecMember = this._member?.member;
        this._community = community;
        this._closeDialog = closeDialog;

        combineLatest([
            this.configurationService.timeConfig,
            this.configurationService.featuresConfig,
            this.apiService.getCustomerConfig(this._community.communityData.id)
        ]).pipe(takeUntil(this._destroyed))
            .subscribe(([timeCfg, featureConfig, customerCfg]) => {
                this._timeZone = timeCfg.time_zone;
                this._featureConfig = featureConfig;
                this._customerCfg = customerCfg;
                this._documentTypes.next([EC_DOCUMENT_TYPE_OTHER].concat(customerCfg.subject_documents?.map(document => document.name) || []));
                this.mkFieldConfigMap(this._customerCfg);
                this.initMemberFormWithConfigurableFields();
                this._participationTypes = this._featureConfig.third_party_producer ? PARTICIPATION_TYPES : [ECMemberParticipationType.MEMBER];
                const memberIsNotThirdPartyProducer = this._ecMember && this._ecMember.participation_type !== ECMemberParticipationType.THIRD_PARTY_PRODUCER;
                if (memberIsNotThirdPartyProducer) {
                    this.setAccessTypesIfMemberIsNotThirdPartProducer();
                } else {
                    this.setAccessTypes(this._ecMember?.participation_type);
                }

                if (this.isUpdate) {
                    this.setFormForUpdate();
                } else {
                    this.setFormForNew();
                }
            });
        this._saving = false;
    }

    initDialogs(spinnerDialog: TemplateRef<any>, adviceDialog: TemplateRef<any>) {
        this._spinnerDialog = spinnerDialog;
        this._adviceDialog = adviceDialog;
    }

    addressChange(event: AddressExt | null) {
        this._currentAddress = mkMemberAddress(event);
        this._currentCoordinates = event ? event.coordinates : null;
        this.addressFormControl?.setValue(this._currentAddress);
    }

    addPropertyUnit() {
        const propertyUnitForm = this.mkEmptyPropertyUnitForm();
        this.propertyUnitsFormArray.push(propertyUnitForm);
    }

    addPod() {
        const podForm = this.mkEmptyPodForm();
        // add configurable land registry fields
        [ConfigurableField.LAND_REGISTRY_SECTION,
            ConfigurableField.LAND_REGISTRY_SHEET,
            ConfigurableField.LAND_REGISTRY_PARTICLE,
            ConfigurableField.LAND_REGISTRY_SUBDIVISION]
            .filter(field => isFieldEnabled(this._fieldConfigs, field))
            .forEach(field => podForm.addControl(field, new FormControl(null, [validateIfMandatory(this._fieldConfigs, field),
                Validators.pattern(LAND_REGISTRY_DATA_PATTERN)])));
        this.listenForPodCadastralDataChanges(podForm);

        // if the member has only one relation with property units,
        // pre-fill the pod land registry data with the data of the property unit
        if (this.propertyUnitControls.length === 1) {
            const propertyUnit = this.propertyUnitControls[0];
            if (podForm.controls[ConfigurableField.LAND_REGISTRY_SECTION]) {
                podForm.controls[ConfigurableField.LAND_REGISTRY_SECTION].setValue(propertyUnit.controls.section.value);
            }
            if (podForm.controls[ConfigurableField.LAND_REGISTRY_SHEET]) {
                podForm.controls[ConfigurableField.LAND_REGISTRY_SHEET].setValue(propertyUnit.controls.sheet.value);
            }
            if (podForm.controls[ConfigurableField.LAND_REGISTRY_PARTICLE]) {
                podForm.controls[ConfigurableField.LAND_REGISTRY_PARTICLE].setValue(propertyUnit.controls.particle.value);
            }
            if (podForm.controls[ConfigurableField.LAND_REGISTRY_SUBDIVISION]) {
                podForm.controls[ConfigurableField.LAND_REGISTRY_SUBDIVISION].setValue(propertyUnit.controls.subdivision.value);
            }
        }

        podForm.controls.podEnergyProfile.valueChanges
            .pipe(takeUntil(this._destroyed))
            .subscribe(value => {
                if (!podExists(podForm)) {
                    if (value === PodEnergyProfiles.PRODUCER || value === PodEnergyProfiles.PROSUMER) {
                        if (plantsFormArray(podForm).length === 0) {
                            this.addPlant(podForm);
                        }
                    } else {
                        plantsFormArray(podForm).clear();
                    }
                    if (value === PodEnergyProfiles.PRODUCER) {
                        podForm.controls.podUtilityType.setValue(null);
                        podForm.controls.podUtilityType.disable();
                    } else {
                        podForm.controls.podUtilityType.enable();
                        podForm.controls.podUtilityType.setValidators(Validators.required);
                    }
                    this.setAccessTypesByEnergyProfiles();
                }
            });

        // take care of initial state (single-point-in-code logic for enabling/disabling form controls)
        this.updatePodDateControls(podForm, null);

        this.listenForPodCodeChanges(podForm);

        podForm.controls.podEnergyProfile.valueChanges
            .pipe(takeUntil(this._destroyed))
            .pipe(startWith(undefined as string), pairwise())
            .subscribe((values: string[]) => {
                if (values[0] !== values[1]) {
                    resetPodStatisticalProfile(podForm);
                }
            });
        this.managePodAddressLikeMemberChange(podForm);
        this.podsFormArray.push(podForm);
        // todo evaluate to use value change in podsFormArray
        this.updatePodEnabledSteps();
        this._accessTypeFormControl.reset();
        this.setAccessTypesByEnergyProfiles();
    }

    addPlant(podForm: FormGroup) {
        const defaultType = PV_PLANT_TYPE;
        const plantCounter = plantsFormArray(podForm).length + 1;
        const plantForm = this.fb.group({
            plantName: new FormControl(this.mkPlantName(defaultType, plantCounter), [validateNotBlank, Validators.pattern(ENTITY_NAME_VALIDATION_PATTERN)]),
            plantType: new FormControl(defaultType, [Validators.required]),
            nominalPower: new FormControl(null, [Validators.required, Validators.min(0)]),
            capitalGrant: new FormControl(0, this.isThirdPartyProducer ? [] : [Validators.required, Validators.min(CAPITAL_GRANT_MIN), Validators.max(CAPITAL_GRANT_MAX)]),
            enterData: new FormControl(null, []),
            incentiveType: new FormControl(null, [Validators.required]),
            profile: new FormControl(null, [Validators.required]),
            predictiveModel: new FormControl(null, []),
            podCode: new FormControl(null, [this.mkPlantPodValidator()], [])
        });

        plantForm.controls.plantType.valueChanges
            .pipe(takeUntil(this._destroyed))
            .subscribe(value => plantForm.controls.plantName.setValue(this.mkPlantName(value, plantCounter)));

        plantForm.controls.nominalPower.valueChanges
            .pipe(takeUntil(this._destroyed),
                startWith(undefined as number),
                pairwise())
            .subscribe((values: number[]) => {
                if (values[0] !== values[1]) {
                    resetPodStatisticalProfile(podForm);
                }
            });

        plantForm.controls.enterData.valueChanges
            .pipe(takeUntil(this._destroyed))
            .subscribe(plantActivationDate => {
                if (plantActivationDate && plantActivationDate.isBefore(moment.tz(UPPER_INCENTIVE_BOUNDARY, this._timeZone))) {
                    plantForm.controls.capitalGrant.setValue(0);
                    plantForm.controls.capitalGrant.disable();
                } else {
                    plantForm.controls.capitalGrant.enable();
                }
                getIncentiveTypes(plantForm, true);  // updates types of incentive based on data entered
            });

        this.updatePlantStatAndPredModOnChange(plantForm);
        resetPodStatisticalProfile(podForm);
        plantsFormArray(podForm).push(plantForm);
    }

    save() {
        if (this._saving) {
            return;
        }
        this._saving = true;
        if (this.isUpdate) {
            this.updateMember().finally(() => this._saving = false);
        } else {
            this.createMember().finally(() => this._saving = false);
        }
    }

    updatePodEnabledSteps() {
        this._energyDataStepEnabled = !this.isUpdate &&
            this.hasPods &&
            this.podControls
                .map(podForm => !podForm.controls.persistentPod.value as boolean) // true if not persistent
                .reduce((p, c) => p || c, false);
        this._statisticalProfileStepEnabled = this._energyDataStepEnabled &&
            this.podControls
                .map(podForm => !podForm.controls.entityName.value as boolean) // true if not persistent
                .reduce((p, c) => p || c, false);
        this.setAccessTypesByEnergyProfiles();
    }

    mkPlantName(type: any, counter: number) {
        const typeName = `${type.name}__{resource}`;
        return `${this.i18n.translate(typeName)} ${counter}`;
    }

    setAccessTypes(participationType?: ECMemberParticipationType) {
        const isAdvancedUser = !!this._featureConfig?.advanced_user;
        const isNotThirdPartyProducer = participationType !== ECMemberParticipationType.THIRD_PARTY_PRODUCER;
        // only member that are not third party producers can have mobile app access
        // advanced access is allowed only if configured
        // none is always available
        this._accessTypes = [
            ...(isNotThirdPartyProducer ? [ECMemberAccessType.MOBILE] : []),
            ...(isAdvancedUser ? [ECMemberAccessType.ADVANCED] : []),
            ECMemberAccessType.NONE
        ];
    }

    mkPlantDefaultStatisticalProfile(nominalPower: number, rateMode: RateMode, type: PlantType): Observable<PlantStatisticalProfile> {
        switch (type) {
            case WIND_PLANT_TYPE:
                return this.apiService.getWindPlantStatisticalProfile({
                    nominal_power: nominalPower,
                    rate_mode: rateMode
                });
            case PV_PLANT_TYPE:
            default:
                return this.apiService.getPvPlantStatisticalProfile({
                    nominal_power: nominalPower,
                    rate_mode: rateMode,
                    zone: this._community.community.zone
                });
        }
    }

    mkPlantDefaultPredictiveModel(nominalPower: number, type: PlantType): Observable<PredictiveModelInfo> {
        switch (type) {
            case WIND_PLANT_TYPE:
                return this.apiService.getWindPlantPredictiveModel({});
            case PV_PLANT_TYPE:
            default:
                return this.apiService.getPvPlantPredictiveModel({nominal_power: nominalPower});
        }
    }

    foreignCountry(): boolean {
        if (isDefined(this._currentAddress)) {
            if (this._currentAddress.region === null && this._currentAddress.country !== null) {
                // custom address case we do not know if the address has a foreign country or not
                // we assume it to be in Italy
                return false;
            }
            return isDefined(this._currentAddress.country) &&
                !ALLOWED_COUNTRIES.includes(this._currentAddress.country);
        }
        return false;
    }

    /**
     * search existing resource pod and link it to administrative pod
     */
    searchResourceAndLinkAdminPod() {
        return Promise.all(this.podControls.map(podForm => {
            const pod = podForm.controls.podCode.value;
            return this.apiService.findEntityByPOD(pod).toPromise().then(result => {
                if (result) {
                    this.hierarchyService.findEntityById(result.id).pipe(first()).toPromise()
                        .then(entity => podForm.controls.entityName.setValue(entity.name));
                } else {
                    if (this.isUpdate && isPersistentPod(podForm)) {
                        podForm.controls.entityName.setValue(podForm.controls.podCode.value);
                    } else {
                        podForm.controls.entityName.setValue(null);
                    }
                }
            });
        }));
    }

    private initForms() {
        this._memberForm = this.fb.group({
            participationType: new FormControl(null, [Validators.required]),
            juridicalType: new FormControl(DEFAULT_JURIDICAL_TYPE, [Validators.required]),
            legalForm: new FormControl(null, []),
            subjectType: new FormControl(null, []),
            entitySubtype: new FormControl(null, []),
            firstName: new FormControl(null, [validateNotBlank, Validators.pattern(NAME_PATTERN)]),
            lastName: new FormControl(null, [validateNotBlank, Validators.pattern(NAME_PATTERN)]),
            documents: this.fb.array([]),
            // not shown in the member wizard step
            sendInvitation: new FormControl(false, [Validators.required])
        }, {
            validators: [validateMemberForm]
        });
        this._podsForm = new FormGroup({pods: new FormArray([])});
        this._propertyUnitsForm = new FormGroup({propertyUnits: new FormArray([])});
    }

    private initCountries() {
        const localizedCountries = COUNTRY_LOCALIZATIONS[this.i18n.language];
        this._countries = localizedCountries ?
            COUNTRIES.map(country => ({code: country.code, name: localizedCountries[country.code] || country.name})) :
            COUNTRIES;
        this._countries.forEach(country => this._countriesByCode[country.code] = country);
    }

    private setFormForNew(): void {
        // when participation type changes, pod controls must be updated
        this._memberForm.controls.participationType.valueChanges
            .pipe(debounceTime(TYPES_DEBOUNCE_MILLIS), takeUntil(this._destroyed))
            .subscribe(() => {
                this.propertyUnitsFormArray.clear();
                this.podsFormArray.controls.forEach((podForm: FormGroup) => {
                    const podCode = podForm.controls.podCode;
                    podForm.controls.seu.setValue(null);
                    if (podCode.valid) {
                        // load all relations involving the POD (possibly with other members)
                        // and update the date controls accordingly
                        this.mkMembersInfo(podCode.value, [this.getStartDateValue(podForm), this.getEndDateValue(podForm)])
                            .then(membersInfo => this.updatePodDateControls(podForm, membersInfo));
                    } else {
                        // if POD code is invalid, we make sure that owners stay disabled for third party producers
                        // later on, when POD code becomes valid, more checks shall be performed (as there may be
                        // other reasons to keep owners disabled)
                        this.updatePodDateControls(podForm, null);
                    }
                });
            });

        // when juridical type changes, the field config must be re-computed, the "base" and "dynamic" fields must be re-evaluated
        this._memberForm.controls.juridicalType.valueChanges
            .pipe(debounceTime(TYPES_DEBOUNCE_MILLIS), takeUntil(this._destroyed))
            .subscribe(() => {
                this.mkFieldConfigMap(this._customerCfg);
                this._memberForm.controls.legalForm.setValue(null);
                this._memberForm.controls.subjectType.setValue(null);
                this._memberForm.controls.entitySubtype.setValue(null);
                this.setMemberFormDynamicControls();
                if (isFieldEnabled(this._fieldConfigs, ConfigurableField.ATECO_CODE)) {
                    this._memberForm.controls[ConfigurableField.ATECO_CODE].setValue(null);
                }
                if (isFieldEnabled(this._fieldConfigs, ConfigurableField.SIGNATORY_TAX_CODE)) {
                    this._memberForm.controls[ConfigurableField.SIGNATORY_TAX_CODE].setValue(null);
                }
            });

        // when subject type changes, entity subtype and legal form must be null
        this._memberForm.controls.subjectType.valueChanges
            .pipe(debounceTime(TYPES_DEBOUNCE_MILLIS), takeUntil(this._destroyed))
            .subscribe(() => {
                this._memberForm.controls.legalForm.setValue(null);  // legal form has no use if we have a subject type
                this._memberForm.controls.entitySubtype.setValue(null);
            });

        // when entity subtype changes, pod utility type of each pod must be null
        this._memberForm.controls.entitySubtype.valueChanges
            .pipe(debounceTime(TYPES_DEBOUNCE_MILLIS), takeUntil(this._destroyed))
            .subscribe(() => this.podControls.forEach(podForm => podForm.controls.podUtilityType.setValue(null)));

        // when legal form changes, the field config must be re-computed and the "dynamic" fields must be re-evaluated
        this._memberForm.controls.legalForm.valueChanges
            .pipe(debounceTime(TYPES_DEBOUNCE_MILLIS), takeUntil(this._destroyed))
            .subscribe(() => {
                this.mkFieldConfigMap(this._customerCfg);
                this.setMemberFormDynamicControls();
            });
    }

    private setFormForUpdate(): void {
        this.setMemberFormFieldsForUpdate();
        this._currentAddress = this._ecMember.personal_data.address;
        this._currentCoordinates = this._ecMember.spatial_coordinates;
        this._accessTypeFormControl.setValue(this._ecMember.access_type);
        this.setAccessTypes(this._ecMember.participation_type);
        this._documentTypes
            .pipe(takeUntil(this._destroyed))
            .subscribe(docTypes => this.setDocumentsFormArrayForUpdate(docTypes));
        this.setPropertyUnitsFormFieldsForUpdate();
        this.setPodsFormFieldsForUpdate();
        this._memberForm.updateValueAndValidity();
    }

    private setAccessTypesIfMemberIsNotThirdPartProducer() {
        forkJoin(this._member.pods.map(pod => this.apiService.findEntityByPOD(pod.pod.pod)
            .pipe(filter(resp => !!resp),
                switchMap(resp => this.apiService.getECPod(this._community.communityData.id, resp.id)),
                map(ecNamedPod => isProducerEntity(pod.resource.entity, ecNamedPod.pod)))))
            .subscribe(isProducer =>
                this.setAccessTypes(isProducer.some(Boolean) ? ECMemberParticipationType.THIRD_PARTY_PRODUCER : null));
    }

    private mkEmptyPropertyUnitForm(): FormGroup {
        // a new property unit has a temporary id
        // if the land registry data matches an already existing property unit,
        // the temporary id must be replaced by the existing one when the member - property unit relation is created
        // otherwise, the temporary id can be used and will be handled by backend
        const formGroup = this.fb.group({
            id: new FormControl(mkPropertyUnitTmpId(), []), // hidden and readonly
            formId: new FormControl(null, []), // hidden and readonly
            communityId: new FormControl(this._community.communityData.id, []), // hidden and readonly
            section: new FormControl(null, [Validators.pattern(LAND_REGISTRY_DATA_PATTERN)]),
            sheet: new FormControl(null, [Validators.required, Validators.pattern(LAND_REGISTRY_DATA_PATTERN)]),
            particle: new FormControl(null, [Validators.required, Validators.pattern(LAND_REGISTRY_DATA_PATTERN)]),
            subdivision: new FormControl(null, [Validators.required, Validators.pattern(LAND_REGISTRY_DATA_PATTERN)]),
            persistentPropertyUnit: new FormControl(false, []), // hidden, empty property unit can be deleted
            startDate: new FormControl(null, []),
            endDate: new FormControl(null, []),
            infoMessage: new FormControl(null, []),
            errorMessage: new FormControl(null, []),
            forbidden: new FormControl(false, [])
        }, {validators: [dateOrderValidator()]});
        this.listenForPropertyUnitChanges(formGroup);
        return formGroup;
    }

    private mkEmptyPodForm() {
        return this.fb.group({
            podCode: new FormControl(null, [validateNotBlank, validatePOD]),
            substationCode: new FormControl(null, []),
            podInfoMessage: new FormControl(null, []),
            podErrorMessage: new FormControl(null, []),
            podCadastralErrorMessage: new FormControl(null, []),
            podAddressLikeMember: new FormControl({value: false, disabled: this.foreignCountry()}, []),
            podAddress: new FormControl('', [Validators.required]),
            aspiringProsumer: new FormControl(null, []),
            // for internal use only
            internalPodAddress: new FormControl(null, []),
            // for internal use only
            internalPodCoordinates: new FormControl(null, []),
            hasWifi: new FormControl(false, []),
            seu: new FormControl(false, []),
            startDate: new FormControl({value: null, disabled: true}),
            endDate: new FormControl({value: null, disabled: true}),
            canResetStartDate: new FormControl(true),
            canResetEndDate: new FormControl(true),
            membersInfo: new FormControl(null, []),
            podProvider: new FormControl('', []),
            creationType: new FormControl(ECPodCreationType.MANUAL, []),
            // third step
            podEnergyProfile: new FormControl(getDefaultEnergyProfile(this.isThirdPartyProducer, this.hasSEU), [Validators.required]),
            podUtilityType: new FormControl('', [Validators.required]),
            podVoltage: new FormControl(VoltageLevel.LOW, [Validators.required]),
            podPhase: new FormControl(Phases.SINGLE, [Validators.required]),
            contractualPower: new FormControl('', []),
            plants: new FormArray([]),
            // NB: if entityName is filled, energy parameters are not editable
            entityName: new FormControl(null, []),
            podName: new FormControl(null, []),
            podEntityEnergyProfile: new FormControl(null, []),
            podExists: new FormControl(false, []),
            persistentPod: new FormControl(false, []),
            predictiveModel: new FormControl(null, []),
            // fourth step
            profile: new FormControl(null, []) // statistical profile
        });
    }

    private mkPropertyUnitFormForUpdate(propertyUnit: ECMemberPropertyUnit, propertyUnitRelation: ECMemberRelation): FormGroup {
        const formId = `${propertyUnit.id}|${propertyUnitRelation.start_date}|${propertyUnitRelation.end_date}`;
        const formGroup = this.fb.group({
            id: new FormControl(propertyUnit.id, []), // hidden and readonly
            formId: new FormControl(formId, []), // hidden and readonly
            communityId: new FormControl(propertyUnit.community_id, []), // hidden and readonly
            section: new FormControl(propertyUnit.section, [Validators.pattern(LAND_REGISTRY_DATA_PATTERN)]), // readonly
            sheet: new FormControl(propertyUnit.sheet, [Validators.required, Validators.pattern(LAND_REGISTRY_DATA_PATTERN)]), // readonly
            particle: new FormControl(propertyUnit.particle, [Validators.required, Validators.pattern(LAND_REGISTRY_DATA_PATTERN)]), // readonly
            subdivision: new FormControl(propertyUnit.subdivision, [Validators.required, Validators.pattern(LAND_REGISTRY_DATA_PATTERN)]), // readonly
            persistentPropertyUnit: new FormControl(true, []), // hidden, property unit can be deleted but not updated
            startDate: new FormControl(propertyUnitRelation?.start_date ? moment.tz(propertyUnitRelation.start_date, this._timeZone) : null),
            endDate: new FormControl(propertyUnitRelation?.end_date ? moment.tz(propertyUnitRelation.end_date, this._timeZone) : null),
            infoMessage: new FormControl(null, []),
            errorMessage: new FormControl(null, []),
            forbidden: new FormControl(isPropertyUnitForbidden(propertyUnit), [])
        }, {validators: [dateOrderValidator()]});
        this._propertyUnitDateMap.set(formId, normalizeRange([propertyUnitRelation?.start_date, propertyUnitRelation?.end_date]));
        this.listenForPropertyUnitChanges(formGroup);
        return formGroup;
    }

    private mkPodFormForUpdate(pod: ECMemberPOD) {
        const podRelation = findMemberTargetPodRelation(this._ecMember.relations, pod.pod);
        return this.fb.group({
            podId: new FormControl(null, []), // readonly
            podInfoMessage: new FormControl(null, []),
            podErrorMessage: new FormControl(null, []),
            podCadastralErrorMessage: new FormControl(null, []),
            podCode: new FormControl(pod.pod, []), // readonly
            substationCode: new FormControl(pod.substation, []),
            podAddressLikeMember: new FormControl(false, []),
            podAddress: new FormControl(googleCompatibleTextAddress(pod.address), []),
            aspiringProsumer: new FormControl(pod.aspiring_prosumer, []),
            // for internal use only
            internalPodAddress: new FormControl(pod.address, []),
            // for internal use only
            internalPodCoordinates: new FormControl(pod.spatial_coordinates, []),
            hasWifi: new FormControl(pod.has_wifi, []),
            seu: new FormControl(isDefined(pod.seu) ? pod.seu : false, []),
            startDate: new FormControl({
                value: podRelation?.start_date ? moment.tz(podRelation.start_date, this._timeZone) : null,
                disabled: true
            }),
            endDate: new FormControl({
                value: podRelation?.end_date ? moment.tz(podRelation.end_date, this._timeZone) : null,
                disabled: true
            }),
            canResetStartDate: new FormControl(!podRelation?.start_date),
            canResetEndDate: new FormControl(!podRelation?.end_date),
            membersInfo: new FormControl(null, []),
            podProvider: new FormControl(pod.energy_provider, []),
            creationType: new FormControl(pod.creation_type, []),
            // third step
            podEnergyProfile: new FormControl(PodEnergyProfiles.CONSUMER, []/*[Validators.required]*/),
            podUtilityType: new FormControl(pod.pod_utility_type, []),
            podVoltage: new FormControl(VoltageLevel.LOW, []/*[Validators.required]*/),
            podPhase: new FormControl(Phases.SINGLE, []/*[Validators.required]*/),
            contractualPower: new FormControl('', []),
            profile: new FormControl(null, []),
            plants: new FormArray([]),
            // NB: if entityName is filled, energy parameters are not editable
            entityName: new FormControl(null, []),
            podName: new FormControl(null, []),
            podEntityEnergyProfile: new FormControl(null, []),
            podExists: new FormControl(true, []),
            // if true pod is already saved into system
            persistentPod: new FormControl(true, []),
            predictiveModel: new FormControl(null, [])
        });
    }

    private setMemberFormFieldsForUpdate() {
        // set not configurable fields
        this._memberForm.controls.participationType.setValue(this._ecMember.participation_type);
        this._memberForm.controls.juridicalType.setValue(this._ecMember.juridical_type);
        this._memberForm.controls.legalForm.setValue(this._ecMember.legal_form);
        this._memberForm.controls.subjectType.setValue(this._ecMember.subject_type);
        this._memberForm.controls.entitySubtype.setValue(this._ecMember.entity_subtype);
        this._memberForm.controls.firstName.setValue(this._ecMember.personal_data.first_name);
        this._memberForm.controls.lastName.setValue(this._ecMember.personal_data.last_name);

        // set basic configurable fields
        setConfigurableControlsValues([
            [ConfigurableField.COMPANY_NAME, this._ecMember.personal_data.company_name],
            [ConfigurableField.MOBILE_NUMBER, this._ecMember.personal_data.mobile_number],
            [ConfigurableField.PHONE_NUMBER, this._ecMember.personal_data.telephone_number],
            [ConfigurableField.SIGNATORY_TAX_CODE, this._ecMember.signatory_tax_code],
            [ConfigurableField.VAT_CODE, this._ecMember.personal_data.piva],
            [ConfigurableField.PEC, this._ecMember.personal_data.pec],
            [ConfigurableField.IBAN, this._ecMember.personal_data.iban],
            [ConfigurableField.ATECO_CODE, this._ecMember.personal_data.ateco_code],
            [ConfigurableField.ADDRESS, googleCompatibleTextAddress(this._ecMember.personal_data.address)],
            [ConfigurableField.SUPPLY_ADDRESS, this._ecMember.personal_data.supply_address ?? null],
            [ConfigurableField.UPSELLING_POD, this._ecMember.personal_data.upselling_pod],
            [ConfigurableField.BIRTH_DATE, this._ecMember.personal_data.birth_date ? moment(this._ecMember.personal_data.birth_date).startOf('day') : null],
            [ConfigurableField.BIRTH_COUNTRY, this._ecMember.personal_data.birth_country ? this._countriesByCode[this._ecMember.personal_data.birth_country] : null],
            [ConfigurableField.BIRTH_MUNICIPALITY, MUNICIPALITIES.find(m => m.name === this._ecMember.personal_data.birth_municipality) ?? null]
        ], this._fieldConfigs, this._memberForm);

        // set remaining configurable fields
        if (isFieldEnabled(this._fieldConfigs, ConfigurableField.EMAIL)) {
            this._memberForm.controls[ConfigurableField.EMAIL].setValue(this._ecMember.personal_data.email);
            this._memberForm.controls.confirmEmail.setValue(this._ecMember.personal_data.email);
        }

        if (isFieldEnabled(this._fieldConfigs, ConfigurableField.TAX_CODE)) {
            this._memberForm.controls[ConfigurableField.TAX_CODE].setValidators(
                this.juridicalType === ECJuridicalType.CORPORATE_ENTITY ?
                    [Validators.pattern(VAT_NUMBER_PATTERN)] :
                    [Validators.pattern(TAX_ID_PATTERN)]);
            this._memberForm.controls[ConfigurableField.TAX_CODE].setValue(this._ecMember.personal_data.cf);
        }
    }

    private setPropertyUnitsFormFieldsForUpdate() {
        const propertyUnitsRelations = this._ecMember.relations.filter(r => r.target_type === ECMemberRelationTargetType.PROPERTY_UNIT);
        propertyUnitsRelations.forEach(propertyUnitRelation =>
            this.propertyUnitsFormArray.push(this.mkPropertyUnitFormForUpdate(this._ecMember.property_units
                .find(pu => pu.id === propertyUnitRelation.target_id), propertyUnitRelation)));
    }

    private setPodsFormFieldsForUpdate() {
        // forbidden ($$...$$) land registry data must not be shown
        // easy: set form field to null. Wrong: in case of update, the forbidden value is lost
        // solution:
        // - keep track of forbidden values
        // - lose them temporarily by setting form field to null
        // - before BE call, when reading form values, set null values of map entries to stored forbidden values, if any
        this._ecMember.pods.forEach(pod => {
            if (!showPropertyUnit(pod.land_registry_data)) {
                this._podAndLrdMap.set(pod.pod, pod.land_registry_data);
            }
            const podForm = this.mkPodFormForUpdate(pod);
            // add configurable land registry fields
            [
                [ConfigurableField.LAND_REGISTRY_SECTION, pod.land_registry_data?.section],
                [ConfigurableField.LAND_REGISTRY_SHEET, pod.land_registry_data?.sheet],
                [ConfigurableField.LAND_REGISTRY_PARTICLE, pod.land_registry_data?.particle],
                [ConfigurableField.LAND_REGISTRY_SUBDIVISION, pod.land_registry_data?.subdivision]
            ].filter(([field, ignore]: [ConfigurableField, string | undefined]) => isFieldEnabled(this._fieldConfigs, field))
                .forEach(([field, value]: [ConfigurableField, string | undefined]) =>
                    podForm.addControl(field, new FormControl(isLandRegistryDataValid(value) ? value : null,
                        [validateIfMandatory(this._fieldConfigs, field), Validators.pattern(LAND_REGISTRY_DATA_PATTERN)])));
            this.listenForPodCadastralDataChanges(podForm);
            this.podsFormArray.push(podForm);
            this.managePodAddressLikeMemberChange(podForm);
            // load all relations involving the POD (possibly with other members)
            // and update the date controls accordingly
            this.mkMembersInfo(pod.pod, [this.getStartDateValue(podForm), this.getEndDateValue(podForm)])
                .then(membersInfo => this.updatePodDateControls(podForm, membersInfo));
        });
    }

    private setDocumentsFormArrayForUpdate(docTypes: string[]) {
        this._ecMember.documents.forEach(doc => {
                const documentForm = this.fb.group({
                    type: new FormControl(docTypes.includes(doc.document_type) ? doc.document_type : EC_DOCUMENT_TYPE_OTHER, [Validators.required]),
                    customType: new FormControl(docTypes.includes(doc.document_type) ? '' : doc.document_type, [Validators.pattern(DOCUMENT_TYPE_PATTERN)]),
                    id: new FormControl(doc.id, null),
                    fileName: new FormControl(doc.filename, null)
                }, {
                    validators: [validateDocumentType]
                });
                this.documentsFormArray.push(documentForm);
            }
        );
    }

    private loadPodRelations(pod: string): Promise<{ [memberId: string]: ECMemberRelation[] }> {
        return this.apiService.getCommunityMemberPodRelationsByPod(pod).toPromise();
    }

    private getStartDateValue(podForm: FormGroup) {
        return podForm.controls.startDate.value?.valueOf() ?? null;
    }

    private getEndDateValue(podForm: FormGroup) {
        return podForm.controls.endDate.value?.valueOf() ?? null;
    }

    private listenForPodCodeChanges(podForm: FormGroup) {
        // pod code validity depends on pod code, start date and end date
        // these 3 controls are the trigger, but it's important to know which control was triggered
        // when pod code changes dates must be cleared
        // the whole validation chain must start only when pod code is syntactically valid
        const podCodeObservable = podForm.controls.podCode.valueChanges
            .pipe(map(podCode => ({source: 'pod', podCode})));
        const dateObservable = merge(podForm.controls.startDate.valueChanges, podForm.controls.endDate.valueChanges)
            .pipe(map(() => ({source: 'date'})));

        let previousPodCode = '';

        merge(podCodeObservable, dateObservable)
            .pipe(
                filter(() => !podIsInvalid(podForm, this.i18n)), // start flow only when pod code is syntactically valid
                withLatestFrom(podCodeObservable),
                map(([trigger, latestPodCode]) => ({podCode: latestPodCode.podCode, source: trigger.source})),
                switchMap(({podCode, source}) => forkJoin([
                    this.getPodInfo(podCode, [this.getStartDateValue(podForm), this.getEndDateValue(podForm)]),
                    this.apiService.getSubstationByPod(podCode)
                ]).pipe(map(([podInfo, substation]) => ({podInfo, source, substation: substation?.id})))), // and get all the relevant information
                takeUntil(this._destroyed))
            .subscribe(({podInfo, source, substation}) => {
                if (substation) {
                    podForm.controls.substationCode.setValue(substation);
                }
                const podCode = podForm.controls.podCode;
                if (source === 'pod' && podCode.value !== previousPodCode) { // clear pod dates on pod code change
                    this.clearPodDates(podForm);
                    previousPodCode = podCode.value;
                }
                // set initial state
                podCode.setErrors(null);
                podForm.controls.podInfoMessage.setValue(null);
                podForm.controls.podErrorMessage.setValue(null);
                podForm.controls.podName.setValue(null);
                podForm.controls.podExists.setValue(null);
                podForm.controls.podEntityEnergyProfile.setValue(null);
                podForm.controls.seu.setValue(null);
                podForm.controls.seu.enable();
                // check pod control and set errors
                validatePodControl(podInfo, podForm, this.podsFormArray, this.isThirdPartyProducer, this.isMobileMember, this.i18n, this.isUpdate);
                // as we now know all relations involving the POD (possibly with other members),
                // we can update the date controls accordingly
                this.updatePodDateControls(podForm, podInfo?.membersInfo);
            });
    }

    private updatePodDateControls(podForm: FormGroup, memberInfo: MembersInfo) {
        // store members info in form in order to be able to later access date boundaries
        podForm.controls.membersInfo.setValue(memberInfo);
    }

    private getPodInfo(pod: string, range: MemberRange): Observable<PodInfo | null> {
        return this.apiService.checkEntityExistenceByPOD(pod).pipe( // check if a POD resource with the given POD code already exists
            switchMap(exists => {
                if (!exists.value) {
                    return of(null); // no `PodInfo` at all if POD does not exist
                }
                return combineLatest([
                    this.apiService.findEntityByPOD(pod), // try to load the *resource* connected to the POD
                    from(this.mkMembersInfo(pod, range)) // load information about *members* potentially related to POD
                ]).pipe(
                    switchMap(([idResponse, membersInfo]) => {
                        if (!idResponse) { // not enough permissions on POD
                            return of({
                                membersInfo // `null` value in case of community POD
                            } as PodInfo);
                        }
                        return this.getPodResource(idResponse.id).pipe( // load information about POD resource
                            map(ecPod => ({
                                podResource: ecPod, // can be `null`, if belonging to another community
                                membersInfo // `null` value in case of community POD
                            } as PodInfo))
                        );
                    })
                );
            })
        );
    }

    private listenForPropertyUnitChanges(propertyUnitForm: FormGroup) {
        merge(
            propertyUnitForm.controls.section.valueChanges.pipe(debounceTime(250)),
            propertyUnitForm.controls.sheet.valueChanges.pipe(debounceTime(250)),
            propertyUnitForm.controls.particle.valueChanges.pipe(debounceTime(250)),
            propertyUnitForm.controls.subdivision.valueChanges.pipe(debounceTime(250)),
            propertyUnitForm.controls.startDate.valueChanges,
            propertyUnitForm.controls.endDate.valueChanges
        ).pipe(
            filter(() => isLandRegistryDataValued(propertyUnitForm)), // land registry data must be valid
            filter(() => !isPropertyUnitLocalDuplicate(this.propertyUnitsFormArray, this.i18n)), // skip validation if the form contains local duplicates
            switchMap(() => this.apiService.getPropertyUnitWithRelationsByLandRegistryValues(this._community.communityData.id, mkPropertyUnitTriplet(propertyUnitForm))
                .pipe(catchError(() => of(null)))),
            takeUntil(this._destroyed)
        ).subscribe(puWithRelations => handlePropertyUnitValidation(puWithRelations, propertyUnitForm, this.i18n, this._propertyUnitDateMap));
    }

    // @PoweredByAi(date="2026-03-05", model="claude-sonnet-4-6")
    private listenForPodCadastralDataChanges(podForm: FormGroup) {
        const sheetCtrl = podForm.controls[ConfigurableField.LAND_REGISTRY_SHEET];
        const particleCtrl = podForm.controls[ConfigurableField.LAND_REGISTRY_PARTICLE];
        const subdivisionCtrl = podForm.controls[ConfigurableField.LAND_REGISTRY_SUBDIVISION];
        const sectionCtrl = podForm.controls[ConfigurableField.LAND_REGISTRY_SECTION];
        if (!sheetCtrl || !particleCtrl || !subdivisionCtrl) {
            return;
        }
        const observables: Observable<any>[] = [
            sheetCtrl.valueChanges.pipe(debounceTime(250)),
            particleCtrl.valueChanges.pipe(debounceTime(250)),
            subdivisionCtrl.valueChanges.pipe(debounceTime(250)),
        ];
        if (sectionCtrl) {
            observables.push(sectionCtrl.valueChanges.pipe(debounceTime(250)));
        }
        merge(...observables).pipe(
            filter(() => !isBlank(sheetCtrl.value) && !isBlank(particleCtrl.value) && !isBlank(subdivisionCtrl.value)),
            switchMap(() => this.apiService.getPropertyUnitWithRelationsByLandRegistryValues(
                this._community.communityData.id,
                {sheet: sheetCtrl.value, particle: particleCtrl.value, subdivision: subdivisionCtrl.value}
            ).pipe(catchError(() => of(null)))),
            takeUntil(this._destroyed)
        ).subscribe(puWithRelations => {
            const errorMessageControl = podForm.controls.podCadastralErrorMessage;
            if (!errorMessageControl) {
                return;
            }
            const sectionValue = sectionCtrl ? mkTextField(sectionCtrl.value) : null;
            const existingSection = puWithRelations ? mkTextField(puWithRelations.section) : null;
            if (puWithRelations && sectionValue !== existingSection) {
                if (isDefined(puWithRelations.section)) {
                    errorMessageControl.setValue(this.i18n.translate(
                        'A property unit with this land registry data is already existing. The section must match \'{0}\' or a new property unit must be entered.',
                        puWithRelations.section
                    ));
                } else {
                    errorMessageControl.setValue(this.i18n.translate(
                        'A property unit with this land registry data is already existing. Remove the section \'{0}\' or enter a new property unit.',
                        sectionCtrl?.value
                    ));
                }
                errorMessageControl.setErrors({[PropertyUnitFormError.DUPLICATE_PROPERTY_UNITS]: true});
            } else {
                errorMessageControl.setValue(null);
                const {[PropertyUnitFormError.DUPLICATE_PROPERTY_UNITS]: removed, ...others} = errorMessageControl.errors || {};
                errorMessageControl.setErrors(Object.keys(others).length ? others : null);
            }
        });
    }

    private managePodAddressLikeMemberChange(podForm: FormGroup) {
        podForm.controls.podAddressLikeMember.valueChanges.pipe(takeUntil(this._destroyed)).subscribe(
            value => {
                if (value) {
                    podForm.controls.podAddress.disable();
                    podForm.controls.podAddress.clearValidators();
                    podForm.controls.podAddress.setValue(this._currentAddress ? googleCompatibleTextAddress(this._currentAddress) : this.addressFormControl?.value ?? null);
                    podForm.controls.internalPodCoordinates.setValue(null);
                    podForm.controls.internalPodAddress.setValue(null);
                } else {
                    podForm.controls.podAddress.enable();
                    podForm.controls.podAddress.setValidators([Validators.required]);
                    podForm.controls.podAddress.setValue(null);
                }
            }
        );
    }

    private getStatProfile(podForm: FormGroup): Promise<{ predictiveModel?: PredictiveModelInfo, profile?: PodStatisticalProfile } | void> {
        const energyProfile = podForm.controls.podEnergyProfile.value as PodEnergyProfiles;
        if (energyProfile === PodEnergyProfiles.CONSUMER) {
            return this.apiService.getConsumerPodPredictiveModel({}).toPromise()
                .then(response => {
                    podForm.controls.predictiveModel.setValue(response);
                    return {profile: podForm.controls.profile.value, predictiveModel: response};
                }).catch(err => {
                    console.error(err);
                    this.msgBoxService.error(this.i18n.translate('Cannot generate predictive model for this POD'));
                });
        }
        if (energyProfile === PodEnergyProfiles.PROSUMER) {
            return this.apiService.getProsumerPodPredictiveModel({}).toPromise()
                .then(response => {
                    podForm.controls.predictiveModel.setValue(response);
                    return {profile: podForm.controls.profile.value, predictiveModel: response};
                }).catch(err => {
                    console.error(err);
                    this.msgBoxService.error(this.i18n.translate('Cannot generate predictive model for this POD'));
                });
        }
        if (energyProfile === PodEnergyProfiles.PRODUCER) {
            const plantsStatisticalProfiles = plantControls(podForm)
                .map(form => ({
                    type: getPodStatisticalProfileRequestPlantType(form.controls.plantType.value),
                    profile: form.controls.profile.value
                }));
            const req: ProducerPodStatisticalProfileRequest = {
                plants: plantsStatisticalProfiles,
                rate_mode: getRateMode(plantsStatisticalProfiles)
            };
            return this.apiService.getProducerPodStatisticalProfile(req).toPromise()
                .then(response => {
                    podForm.controls.predictiveModel.setValue(null);
                    podForm.controls.profile.setValue(response);
                    return {profile: response, predictiveModel: null};
                }).catch(err => {
                    console.error(err);
                    this.msgBoxService.error(this.i18n.translate('Cannot generate statistical profile for this POD'));
                });
        }
        return Promise.resolve({predictiveModel: null, profile: null});
    }

    private podCode(podForm: FormGroup): string {
        return podForm.controls.podCode.value;
    }

    /**
     * Calculate plant statistical profile and predictive model on nominalPower change
     */
    private updatePlantStatAndPredModOnChange(plantForm: FormGroup) {
        plantForm.controls.nominalPower.valueChanges
            .pipe(takeUntil(this._destroyed),
                filter(isDefined),
                tap(() => this._energyDataStepBusy = true),
                debounceTime(500),
                switchMap(nominalPower => forkJoin([
                    this.mkPlantDefaultStatisticalProfile(nominalPower, RateMode.SINGLE, getPlantType(plantForm)),
                    this.mkPlantDefaultPredictiveModel(nominalPower, getPlantType(plantForm))])))
            .subscribe(([plantStatisticalProfile, plantPredictiveModel]) =>
                this.onNominalPowerOrPlantTypeChange(plantForm, plantStatisticalProfile, plantPredictiveModel));

        plantForm.controls.plantType.valueChanges
            .pipe(takeUntil(this._destroyed),
                filter(isDefined),
                tap(() => this._energyDataStepBusy = true),
                debounceTime(500),
                switchMap(() => forkJoin([
                    this.mkPlantDefaultStatisticalProfile(plantForm.controls.nominalPower.value, RateMode.SINGLE, getPlantType(plantForm)),
                    this.mkPlantDefaultPredictiveModel(plantForm.controls.nominalPower.value, getPlantType(plantForm))])))
            .subscribe(([plantStatisticalProfile, plantPredictiveModel]) =>
                this.onNominalPowerOrPlantTypeChange(plantForm, plantStatisticalProfile, plantPredictiveModel));
    }

    private onNominalPowerOrPlantTypeChange(plantForm: FormGroup,
                                            plantStatisticalProfile: PlantStatisticalProfile,
                                            plantPredictiveModel: PredictiveModelInfo) {
        if (plantForm.controls.plantType.value === PV_PLANT_TYPE) {
            plantForm.controls.profile.setValue(plantStatisticalProfile);
        } else if (plantForm.controls.plantType.value === WIND_PLANT_TYPE) {
            plantForm.controls.profile.reset();
        }
        plantForm.controls.predictiveModel.setValue(plantPredictiveModel);
        this._energyDataStepBusy = false;
    }

    private mkPersonalData(mode: 'CREATE' | 'UPDATE'): ECMemberPersonalData {
        let email = '';
        if (mode === 'UPDATE' && this._ecMember.personal_data.email) {
            email = this._ecMember.personal_data.email;
        } else if (mode === 'UPDATE' && !this._ecMember.personal_data.email || mode === 'CREATE') {
            email = this._memberForm.controls[ConfigurableField.EMAIL]?.value && this._memberForm.controls[ConfigurableField.EMAIL].value !== '' ?
                this._memberForm.controls[ConfigurableField.EMAIL].value : null;
        }
        return {
            email,
            company_name: this.isCompany ? this._memberForm.controls[ConfigurableField.COMPANY_NAME]?.value : null,
            first_name: this._memberForm.controls.firstName.value,
            last_name: this._memberForm.controls.lastName.value,
            cf: mkTextField(this._memberForm.controls[ConfigurableField.TAX_CODE]?.value),
            piva: this.isCompany ? mkTextField(this._memberForm.controls[ConfigurableField.VAT_CODE]?.value) : null,
            pec: mkTextField(this._memberForm.controls[ConfigurableField.PEC]?.value),
            iban: mkTextField(this._memberForm.controls[ConfigurableField.IBAN]?.value),
            ateco_code: mkTextField(this._memberForm.controls[ConfigurableField.ATECO_CODE]?.value),
            birth_date: this._memberForm.controls[ConfigurableField.BIRTH_DATE]?.value ? this._memberForm.controls[ConfigurableField.BIRTH_DATE].value.valueOf() : null,
            birth_country: this._memberForm.controls[ConfigurableField.BIRTH_COUNTRY]?.value?.code ?? null,
            birth_municipality: this._memberForm.controls[ConfigurableField.BIRTH_MUNICIPALITY]?.value?.name ?? null,
            address: this._currentAddress,
            mobile_number: mkTextField(this._memberForm.controls[ConfigurableField.MOBILE_NUMBER]?.value),
            telephone_number: mkTextField(this._memberForm.controls[ConfigurableField.PHONE_NUMBER]?.value),
            upselling_pod: mkTextField(this._memberForm.controls[ConfigurableField.UPSELLING_POD]?.value),
            supply_address: mkTextField(this._memberForm.controls[ConfigurableField.SUPPLY_ADDRESS]?.value)
        };
    }

    private mkNewECMemberRequest(personalData: ECMemberPersonalData,
                                 pods: ECMemberPOD[],
                                 propertyUnits: ECMemberPropertyUnit[]): NewECMemberRequest {
        return {
            network_id: this._community.communityData.id,
            juridical_type: this.juridicalType,
            legal_form: this.legalFormType,
            subject_type: this.subjectType,
            entity_subtype: this.entitySubtype,
            personal_data: personalData,
            spatial_coordinates: this._currentCoordinates,
            pods,
            relations: [...mkPropertyUnitRelations(this.propertyUnitControls), ...mkPodRelations(this.podControls)],
            property_units: propertyUnits,
            participation_type: this.participationType,
            access_type: this.getAccessType,
            signatory_tax_code: mkTextField(this._memberForm.controls[ConfigurableField.SIGNATORY_TAX_CODE]?.value)
        };
    }

    private mkUpdateECMemberRequest(personalData: ECMemberPersonalData,
                                    pods: ECMemberPOD[],
                                    propertyUnits: ECMemberPropertyUnit[]): UpdateECMemberRequest {
        return {
            u3id: this._ecMember.u3id,
            network_id: this._community.communityData.id,
            juridical_type: this._ecMember.juridical_type,
            legal_form: this._ecMember.legal_form,
            subject_type: this._ecMember.subject_type,
            entity_subtype: this._ecMember.entity_subtype || this._memberForm.controls.entitySubtype.value,
            personal_data: personalData, // ECMemberPersonalData,
            accepted_documents: this._ecMember.accepted_documents,
            spatial_coordinates: this._currentCoordinates, // SpatialCoordinates
            pods,
            relations: [...mkPropertyUnitRelations(this.propertyUnitControls), ...mkPodRelations(this.podControls)],
            property_units: propertyUnits,
            participation_type: this._ecMember.participation_type,
            access_type: this.getAccessType,
            signatory_tax_code: mkTextField(this._memberForm.controls[ConfigurableField.SIGNATORY_TAX_CODE]?.value)
        };
    }

    private async createMember() {
        const sendInvitation = this._memberForm.controls.sendInvitation.value;
        const personalData = this.mkPersonalData('CREATE');
        const pods = await Promise.all(this.podControls.map(podForm => this.mkEcMemberPodPromise(podForm)));
        const propertyUnits = this.mkEcMemberPropertyUnits(this.propertyUnitControls);
        const member = this.mkNewECMemberRequest(personalData, pods, propertyUnits);
        const spinner = this.openSpinner('We are creating the subject');
        try {
            const response = await this.apiService.createMember(member);
            if (sendInvitation) {
                await this.apiService.inviteMember(response.id)
                    .catch(err => this.msgBoxService.error(this.i18n.translate('Could not send invitation to subject'), err));
            }
            if (Object.keys(this._documents).length > 0) {
                await this.uploadDocuments(response.id);
            }
            this._closeDialog.emit(true);
            spinner.context.next({
                loading: false,
                loadingMessage: this.i18n.translate('Subject successfully created')
            });
            spinner.closed.then(() => this.dialogService.open<any, any>({template: this._adviceDialog}));
        } catch (err) {
            const errMsg = this.i18n.translate(mkErrorMessage(err, 'An error occurred while creating subject'));
            spinner.context.next({
                loading: false,
                loadingMessage: errMsg,
                abort: true
            });
            this.msgBoxService.error(errMsg, err);
            throw err;
        }
    }

    private uploadDocuments(memberId: string): Promise<(ECMemberDocument | void)[]> {
        return Promise.all(this.documentControls.map(form => {
            const fileName = form.controls.fileName.value;
            const type = form.controls.type.value;
            const customType = form.controls.customType.value;
            const id = form.controls.id.value;
            const document = this._documents[id];
            const file = {filename: fileName, content: document.file};
            // if the provided type matches one of the suggested types, use it as the type
            // otherwise, use the type inserted by the user in the custom type field
            return this.apiService.createMemberDocument(memberId, file,
                type === EC_DOCUMENT_TYPE_OTHER ? customType : type)
                .catch(err => console.error(`Cannot upload file: ${fileName}`, err));
        }));
    }

    private prepareDocuments(): EditedDocument[] {
        return this.documentControls.map(docForm => {
            const docId = docForm.controls.id.value;
            const type = docForm.controls.type.value;
            const customType = docForm.controls.customType.value;
            return {
                documentId: docId,
                documentType: type === EC_DOCUMENT_TYPE_OTHER ? customType : type,
                file: this._documents[docId]?.file
            };
        });
    }

    private updateMemberDocuments(member: ECMember, editedDocs: EditedDocument[]): Promise<ECMember> {
        const newDocuments = editedDocs.filter(doc => doc.documentId.startsWith(INTERNAL_DOCUMENT_PREFIX));
        const updatedDocuments = editedDocs.filter(doc => !doc.documentId.startsWith(INTERNAL_DOCUMENT_PREFIX) && !isSameDocument(doc, member.documents.find(m => m.id === doc.documentId)));
        const removedDocuments = member.documents.filter(doc => !editedDocs.find(ed => ed.documentId === doc.id));
        if (!newDocuments.length && !updatedDocuments.length && !removedDocuments.length) {
            return Promise.resolve(member);
        }
        // NOTE: after all changes have been committed, the member is reloaded
        return Promise.all([
            // create new documents
            ...(newDocuments.map(doc => this.apiService.createMemberDocument(member.id, {
                content: doc.file,
                filename: doc.file.name
            }, doc.documentType, doc.metadata, doc.expiration).then(() => {
            }))),
            // update already existing documents
            ...(updatedDocuments.map(doc => this.apiService.updateMemberDocument(member.id, doc.documentId,
                doc.file ? {
                    content: doc.file,
                    filename: doc.file.name
                } : null, doc.documentType, doc.metadata, doc.expiration))),
            // delete removed documents
            ...(removedDocuments.map(doc => this.apiService.deleteMemberDocument(member.id, doc.id)))
        ]).then(() => this.apiService.loadMember(member.id).toPromise());
    }

    private mkEcMemberPropertyUnits(propertyUnitControls: FormGroup[]): ECMemberPropertyUnit[] {
        const now = moment.tz(this._timeZone).valueOf();
        // property_units of NewECMemberRequest must not contain duplicates on land registry data triplet
        // time ranges are ignored here
        const propertyUnitLookup: Map<string, ECMemberPropertyUnit> = new Map();
        const uniquePropertyUnits: ECMemberPropertyUnit[] = [];
        propertyUnitControls
            .forEach(propertyUnitForm => {
                const key = mkPropertyUnitKey(propertyUnitForm);
                if (propertyUnitLookup.has(key)) {
                    // property unit is duplicated
                    // form control id value is updated for further usage (e.g. to make relations) so that all duplicated property units have the same id
                    propertyUnitForm.controls.id.setValue(propertyUnitLookup.get(key));
                } else {
                    propertyUnitLookup.set(key, propertyUnitForm.controls.id.value);
                    uniquePropertyUnits.push({
                        id: propertyUnitForm.controls.id.value,
                        community_id: this._community.communityData.id,
                        section: mkTextField(propertyUnitForm.controls.section.value),
                        sheet: mkTextField(propertyUnitForm.controls.sheet.value),
                        particle: mkTextField(propertyUnitForm.controls.particle.value),
                        subdivision: mkTextField(propertyUnitForm.controls.subdivision.value),
                        created_at: now
                    });
                }
            });
        return uniquePropertyUnits;
    }


    /**
     * create an EcMemberPod with resources
     */
    private async createEcMemberPodFull(podForm: FormGroup): Promise<ECMemberPOD> {
        const profiles = await this.getStatProfile(podForm);
        const plants = plantControls(podForm)
            .map(formPlant => mkEnergyCommunityNamedPlant(podForm, formPlant, this.isThirdPartyProducer, this._timeZone));
        const newPod = mkNewPod(podForm, profiles, this.podCode(podForm));
        const energyCommunityNamedPod = mkEnergyCommunityNamedPod(podForm, newPod, plants);
        return createEcMemberPod(podForm, energyCommunityNamedPod, this._currentAddress, this._currentCoordinates, this._podAndLrdMap);
    }

    private async updateMember(): Promise<void> {
        await this.searchResourceAndLinkAdminPod();
        const sendInvitation = this._memberForm.controls.sendInvitation.value;
        const personalData = this.mkPersonalData('UPDATE');
        if (this._memberForm.controls.entitySubtype.value !== this._ecMember.entity_subtype) {
            // If we changed the subtype, then we need to change the pod utility type of all user pods
            // to the ones that are actually supported by the new subtype
            const availableArr = getPodUtilityTypes(this.juridicalType,
                this._memberForm.controls.legalForm.value, this._memberForm.controls.subjectType.value,
                this._memberForm.controls.entitySubtype.value);
            if (!availableArr.includes(ECPodUtilityType.OTHER)) {  // if new subtype doesn't include other, then change it
                this.podControls.forEach(podForm => podForm.controls.podUtilityType.setValue(availableArr[0]));  // take the first one available
            }
        }
        let hasNewPods = false;
        const asyncPods = this.podControls
            .map(podForm => {
                if (isPersistentPod(podForm)) {
                    return Promise.resolve(createEcMemberPod(podForm, null, this._currentAddress, this._currentCoordinates, this._podAndLrdMap));
                }
                hasNewPods = true;
                return this.mkEcMemberPodPromise(podForm);
            });
        const pods = await Promise.all(asyncPods);
        const propertyUnits = this.mkEcMemberPropertyUnits(this.propertyUnitControls);
        const request = this.mkUpdateECMemberRequest(personalData, pods, propertyUnits);
        const spinner = this.openSpinner('We are updating the subject');
        try {
            await this.apiService.updateMember(this._ecMember.id, request, true);
            if (sendInvitation) {
                await this.apiService.inviteMember(this._ecMember.id)
                    .catch(err => this.msgBoxService.error(this.i18n.translate('Could not send invitation to subject'), err));
            }

            await this.updateMemberDocuments(this._ecMember, this.prepareDocuments());

            this._closeDialog.emit(true);
            spinner.context.next({
                loading: false,
                loadingMessage: this.i18n.translate('Subject updated')
            });
            spinner.closed.then(() => {
                if (hasNewPods) {
                    this.dialogService.open<any, any>({
                        template: this._adviceDialog
                    });
                }
            });
        } catch (err) {
            const errMsg = this.i18n.translate(mkErrorMessage(err, 'An error occurred while updating subject'));
            spinner.context.next({
                loading: false,
                loadingMessage: errMsg,
                abort: true
            });
            this.msgBoxService.error(errMsg, err);
            throw err;
        }
    }

    private openSpinner(message: string) {
        const spinnerData = new BehaviorSubject<SpinnerData>({
            loading: true,
            loadingMessage: this.i18n.translate(message)
        });
        return this.dialogService.open<Subject<SpinnerData>, undefined>({
            template: this._spinnerDialog,
            context: spinnerData
        }, {
            width: 700,
            hideCloseButton: true
        });
    }

    private mkPlantPodValidator(): ValidatorFn {
        return ctrl => {
            const podErr = productionPODValidator(ctrl);
            if (podErr) {
                return podErr;
            }
            if (isBlank(ctrl.value)) {
                // no need to check uniqueness
                return null;
            }
            // check for production POD uniqueness
            const otherProductionPods: string[] = [];
            this.podControls.forEach(podForm => plantControls(podForm).forEach(plantForm => {
                if (plantForm.controls.podCode !== ctrl) {
                    otherProductionPods.push(plantForm.controls.podCode.value);
                }
            }));
            if (otherProductionPods.find(p => p?.trim() === ctrl.value)) {
                return {[FormError.UNIQUE_POD]: true};
            }
            return null;
        };
    }

    private setAccessTypesByEnergyProfiles() {
        const atLeastOnePodProducer = this.podControls
            .map(podForm => {
                    const isProducer = (podForm.controls.podEnergyProfile.value as PodEnergyProfiles) === PodEnergyProfiles.PRODUCER ||
                        (podForm.controls.podEntityEnergyProfile.value as PodEnergyProfiles) === PodEnergyProfiles.PRODUCER;

                    const isSEU = ((podForm.controls.podEnergyProfile.value as PodEnergyProfiles) === PodEnergyProfiles.PROSUMER ||
                            (podForm.controls.podEntityEnergyProfile.value as PodEnergyProfiles) === PodEnergyProfiles.PROSUMER) &&
                        (podForm.controls.seu.value as boolean) === true;
                    return isProducer || isSEU;
                }
            ).reduce((acc, val) => acc || val, false);
        this.setAccessTypes(atLeastOnePodProducer ? ECMemberParticipationType.THIRD_PARTY_PRODUCER : null);
    }

    private mkEcMemberPodPromise(podForm: FormGroup): Promise<ECMemberPOD> {
        return podForm.controls.entityName.value ?
            Promise.resolve(createEcMemberPod(podForm, null, this._currentAddress, this._currentCoordinates, this._podAndLrdMap)) :
            this.createEcMemberPodFull(podForm);
    }

    private getPodResource(podId: string): Observable<EnergyCommunityNamedPod | null> {
        return this.apiService.getECPod(this._community.communityData.id, podId).pipe(
            catchError(err => {
                if (errorCode(err) === ErrorCode.POD_NOT_PART_OF_GIVEN_COMMUNITY) {
                    // if POD actually belongs to another community, mask it under `null`
                    return of(null);
                }
                // all other errors are normally propagated
                return throwError(() => err);
            })
        );
    }

    private async mkMembersInfo(pod: string, range: MemberRange): Promise<MembersInfo> {
        const podRelations = await this.loadPodRelations(pod);
        const holderMembers = getPodHolders(podRelations);
        const rangeTakenBy = rangeTaken(holderMembers, range, this._timeZone, ALLOW_FUTURE_MEMBER_POD_RELATIONS);
        const holderMembersInfo: MembersInfo = {
            members: holderMembers, // no need to store names here
            rangeTakenBy
        };
        if (!rangeTakenBy) {
            return holderMembersInfo;
        }
        // if some range is taken, enrich it with information about involved member's name
        const members = await this.apiService.findMembersByPOD(pod).toPromise();
        // NOTE: some members may not have been found due to lack of permissions
        const member = members.results.find(ecMember => ecMember.id === rangeTakenBy.memberId);
        if (member) {
            holderMembersInfo.rangeTakenBy.memberName = mkMemberName(member);
        }
        return holderMembersInfo;
    }

    private clearPodDates(podForm: FormGroup) {
        podForm.controls.startDate.setValue(null, {emitEvent: false});
        podForm.controls.endDate.setValue(null, {emitEvent: false});
        podForm.controls.membersInfo.setValue(null);
    }


    private mkFieldConfigMap(customerCfg: CustomerConfig) {
        this._fieldConfigs = ((customerCfg.configurable_fields || {})[FieldConfigScope.CONTROL_PANEL] || [])
            .reduce((fieldConfigMap, fieldConfig) => fieldConfigMap.set(fieldConfig.field, fieldConfig), new Map<ConfigurableField, FieldConfig>());
        const deepFieldConfig = ((customerCfg.deep_configurable_fields || {})[FieldConfigScope.CONTROL_PANEL] || []);
        const member = mkMemberForConfig(this._ecMember, this.juridicalType, this.legalFormType, this.subjectType);
        const overridingFieldConfigs = getMatchedConditions(deepFieldConfig, member);
        overridingFieldConfigs.forEach(config => this._fieldConfigs.set(config.field, config));
    }

    private initMemberFormWithConfigurableFields() {
        // add configurable fields with eventual mandatory validation
        addConfigurableControls([ConfigurableField.ADDRESS, ConfigurableField.SUPPLY_ADDRESS, ConfigurableField.BIRTH_DATE],
            this._fieldConfigs, this._memberForm);

        // add configurable fields with eventual mandatory validation and pattern validation
        addConfigurableControlsWithAdditionalValidator([
                [ConfigurableField.PEC, Validators.pattern(EMAIL_PATTERN)],
                [ConfigurableField.MOBILE_NUMBER, Validators.pattern(MOBILE_PATTERN)],
                [ConfigurableField.PHONE_NUMBER, Validators.pattern(MOBILE_PATTERN)],
                [ConfigurableField.IBAN, Validators.pattern(IBAN_PATTERN)],
                [ConfigurableField.UPSELLING_POD, validatePOD],
            ],
            this._fieldConfigs, this._memberForm);

        // add more elaborate configurable fields
        if (isFieldEnabled(this._fieldConfigs, ConfigurableField.EMAIL)) {
            this._memberForm.addControl(ConfigurableField.EMAIL, new FormControl(null,
                [validateIfMandatory(this._fieldConfigs, ConfigurableField.EMAIL), Validators.pattern(EMAIL_PATTERN)]));
            this._memberForm.addControl('confirmEmail', new FormControl(null,
                [validateIfMandatory(this._fieldConfigs, ConfigurableField.EMAIL), Validators.pattern(EMAIL_PATTERN)]));
        }

        if (isFieldEnabled(this._fieldConfigs, ConfigurableField.BIRTH_COUNTRY)) {
            this._memberForm.addControl(ConfigurableField.BIRTH_COUNTRY, new FormControl(null,
                [validateIfMandatory(this._fieldConfigs, ConfigurableField.BIRTH_COUNTRY)]));
            this._memberForm.controls[ConfigurableField.BIRTH_COUNTRY].valueChanges
                .pipe(debounceTime(TYPES_DEBOUNCE_MILLIS), takeUntil(this._destroyed))
                .subscribe(() =>
                    setConfigurableDynamicControl(this._memberForm, this._fieldConfigs, ConfigurableField.BIRTH_MUNICIPALITY,
                        [this.bornInItaly ? validateIfMandatory(this._fieldConfigs, ConfigurableField.BIRTH_MUNICIPALITY) : () => null]));
        }

        this.setMemberFormDynamicControls();
    }

    private setMemberFormDynamicControls() {
        // mobile number
        setConfigurableDynamicControl(this._memberForm, this._fieldConfigs, ConfigurableField.MOBILE_NUMBER,
            [Validators.pattern(MOBILE_PATTERN), validateIfMandatory(this._fieldConfigs, ConfigurableField.MOBILE_NUMBER)]);

        // ateco code
        setConfigurableDynamicControl(this._memberForm, this._fieldConfigs, ConfigurableField.ATECO_CODE,
            [Validators.pattern(ATECO_PATTERN),
                this.isCompany ? validateIfMandatory(this._fieldConfigs, ConfigurableField.ATECO_CODE) : () => null]);

        // company name
        setConfigurableDynamicControl(this._memberForm, this._fieldConfigs, ConfigurableField.COMPANY_NAME,
            [this.isCompany ? validateIfMandatory(this._fieldConfigs, ConfigurableField.COMPANY_NAME) : () => null]);

        // tax code
        setConfigurableDynamicControl(this._memberForm, this._fieldConfigs, ConfigurableField.TAX_CODE,
            [this.juridicalType === ECJuridicalType.CORPORATE_ENTITY ?
                Validators.pattern(VAT_NUMBER_PATTERN) :
                Validators.pattern(TAX_ID_PATTERN),
                validateIfMandatory(this._fieldConfigs, ConfigurableField.TAX_CODE)]);

        // vat code
        setConfigurableDynamicControl(this._memberForm, this._fieldConfigs, ConfigurableField.VAT_CODE,
            [Validators.pattern(VAT_NUMBER_PATTERN),
                this.isCompany ? validateIfMandatory(this._fieldConfigs, ConfigurableField.VAT_CODE) : () => null]);

        // signatory tax code
        setConfigurableDynamicControl(this._memberForm, this._fieldConfigs, ConfigurableField.SIGNATORY_TAX_CODE,
            [Validators.pattern(TAX_ID_PATTERN),
                this.isCorporateEntity ? validateIfMandatory(this._fieldConfigs, ConfigurableField.SIGNATORY_TAX_CODE) : () => null]);
    }

}
